/**
 * AI Referrer Tracking
 *
 * Detects visits originating from AI assistants (ChatGPT, Perplexity, Gemini,
 * Copilot, Claude) and fires analytics events so we can measure how much
 * traffic comes from AI-generated answers.
 *
 * Usage:
 *   import { trackAIReferral } from '@/utils/ai-referrer-tracking'
 *
 *   // Call once on page load (e.g. in gatsby-browser.ts onRouteUpdate)
 *   trackAIReferral()
 */

/** Domains used by major AI chat products as of 2026 */
export const AI_REFERRER_PATTERNS = [
  'chat.openai.com',
  'chatgpt.com',
  'perplexity.ai',
  'gemini.google.com',
  'copilot.microsoft.com',
  'claude.ai',
  'you.com',
  'phind.com',
  'bing.com/chat',
] as const

/**
 * Check whether a referrer string belongs to an AI assistant.
 */
export function isAIReferrer(referrer: string): boolean {
  if (!referrer) return false
  const lower = referrer.toLowerCase()
  return AI_REFERRER_PATTERNS.some((pattern) => lower.includes(pattern))
}

/**
 * Detect AI referral from `document.referrer` and send a GA4 event.
 * Safe to call in SSR (no-ops when `window` is undefined).
 */
export function trackAIReferral(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  const referrer = document.referrer
  if (!isAIReferrer(referrer)) return

  // Determine which AI source
  const source =
    AI_REFERRER_PATTERNS.find((p) => referrer.toLowerCase().includes(p)) ??
    'unknown-ai'

  // GA4 event (gtag.js)
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'ai_referral', {
      event_category: 'SEO',
      event_label: source,
      page_path: window.location.pathname,
    })
  }

  // Also push to dataLayer for GTM users
  if (Array.isArray((window as any).dataLayer)) {
    ;(window as any).dataLayer.push({
      event: 'ai_referral',
      ai_source: source,
      page_path: window.location.pathname,
    })
  }
}
